import React, { useState, useContext } from "react";
import {
  Flex,
  FormControl,
  Input,
  FormLabel,
  Button,
  IconButton,
  InputRightElement,
  FormHelperText,
  Heading,
  InputGroup, Container, Spinner, useToast
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import { IconEye } from "@assets/Icons";
import Layout from "@pages/Layout";
import { Separator, ErrorBox } from "@components";
import { resetPasswordSchema } from "@lib/validationData";
import { changePassword } from "@lib/datahandlers/company";
import { isInvalidHandler, showPasswordHandler } from "@lib/functions";
import { t } from "@lang/i18n";

const ResetPassword = () => {
  const initShowPassword = { password: false, new_password: false };

  const [isLoaded, setIsLoaded] = useState(false);
  const [showPassword, setShowPassword] = useState(initShowPassword);

  const history = useHistory();
  const toast = useToast();
  const { register, handleSubmit, getValues, formState: { errors } } = useForm({
    resolver: yupResolver(resetPasswordSchema)
  });

  const submitFormWithKeyboard = (e) => {
    if (e.code === 'Enter') {
      onReset({
        password: getValues('password'),
        new_password: getValues('new_password'),
      })
    }
  };
  const handleShowClick = (fieldType) => {
    if (fieldType === "password") {
      setShowPassword({
        ...showPassword,
        password: !showPassword.password,
      });
    } else {
      setShowPassword({
        ...showPassword,
        new_password: !showPassword.new_password,
      });
    }
  };

  const onReset = async (data) => {
    setIsLoaded(true);
    const result = await changePassword(data);

    if (result.success) {
      setIsLoaded(false);
      toast({ 
        description: t('messages.success.reset_passowrd'),
        status: "success", position: "bottom-right", variant: "left-accent", isClosable: true 
      })
      history.push("/candidate/profile");
      return;
    }
    setIsLoaded(false);
    toast({ 
      description: t('messages.error.reset_passowrd'),
      status: "error", position: "bottom-right", variant: "left-accent", isClosable: true 
    })
  };

  return (
    <Layout isNavBarVisible isFooterVisible menuType="company">
      <Container maxW="429px" bg="white" minHeight='calc(100vh - 100px)' py="39px" px={["16px","","","42px"]} m="63px auto 129px auto" borderRadius="4px">
        <Flex flexDir="column" backdropBlur="blur(10px)" opacity=".9">
          <Heading as="h1" fontSize="30px" color="black" fontWeight="700" textAlign="center">
            {t("company.profile.reset_password.title")}
          </Heading>

          <Separator />

          <form onSubmit={handleSubmit(onReset)}>
            <Flex flexDir="column" alignItems="center" justifyContent="center">
              <FormControl id="password" maxW="345px">
                <FormLabel htmlFor="password" fontWeight="300">
                  {t("company.profile.reset_password.current_password")}
                </FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword.password ? "text" : "password"}
                    placeholder={t("company.profile.reset_password.current_password")}
                    isInvalid={errors.password ?  true : false}
                    focusBorderColor={errors.password ? "error" : "green.main"}
                    errorBorderColor="error"
                    borderColor="gray.borders"
                    borderRadius="4px"
                    bg="white"
                    mb="2px"
                    maxW="345px"
                    h="46px"
                    {...register("password")}
                  />
                  <InputRightElement top="10%" right="2%">
                    <IconButton
                      onClick={() => handleShowClick("password")}
                      icon={ <IconEye opened={showPasswordHandler(showPassword.password)} /> }
                      bg="invisible" _hover={{ bg: "invisible" }} _focus={{ outline: "none" }} _active={{ bg: "white" }} />
                  </InputRightElement>
                </InputGroup>
                {errors.password && <ErrorBox error={errors.password.message} />}
              </FormControl>

              <Separator m="1.5rem auto 1.5rem auto" maxW="340px" />

              <FormControl id="new_password"  maxW="345px">
                <FormLabel htmlFor="new_password" fontWeight="300">
                  { t('company.profile.reset_password.new_password') }
                </FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword.new_password ? "text" : "password"}
                    placeholder={ t('company.profile.reset_password.new_password') }
                    isInvalid={errors.new_password ?  true : false}
                    focusBorderColor={errors.new_password ? "error" : "green.main"}
                    errorBorderColor="error"
                    borderColor="gray.borders"
                    borderRadius="4px" bg="white" mb="2px" maxW="345px" h="46px"
                    {...register("new_password")}
                  />
                  <InputRightElement top="10%" right="2%">
                    <IconButton
                      onClick={() => handleShowClick("new_password")}
                      icon={ <IconEye opened={showPasswordHandler(showPassword.new_password)} /> } 
                      bg="invisible"  _hover={{ bg: "invisible" }}
                    />
                  </InputRightElement>
                </InputGroup>
                {errors.new_password && <ErrorBox error={errors.new_password.message} />}
                <FormHelperText textAlign="left" fontWeight="300" fontSize="12px" mr="1rem">
                  { t('company.profile.reset_password.helper') }
                </FormHelperText>
              </FormControl>
            </Flex>

            <Button type="submit" w={{base: "60vw", md: "344px"}} h="40px" borderRadius="24px" border="2px solid" borderColor="green.main" m="26px auto 0 auto" bg="green.main" color="white" display="flex"
              _hover={{ bg: "green.light", borderColor: "green.light" }}>
              {isLoaded? <Spinner size="md" /> : t('global.confirm_1')}
            </Button>
          </form>
        </Flex>
      </Container>
    </Layout>
  );
};

export default ResetPassword;
